"use client";

import { useEffect, useState } from "react";

import FeatureAnnouncement from "@/components/FeatureAnnouncement";
import { Kbd, KeyCombo } from "@/components/ui/kbd";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { useFeatureAnnouncement } from "@/hooks/useFeatureAnnouncement";
import { pageShortcutKeys, searchKeys } from "@/lib/keyboard";

// Only where there is a keyboard and the icon rail is showing (md and up).
const KEYBOARD_SCREEN = "(pointer: fine) and (min-width: 768px)";

/**
 * The first tour asked under "nav_search_shortcuts". A seen flag is written
 * once and never reset, so anyone whose browser already holds that one can
 * never be shown the tour again — including the people it closed on before
 * they read it. The rebuilt tour asks under a new name, so everybody gets it
 * once more.
 */
const TOUR_ID = "nav_search_shortcuts_tour";

/**
 * The one-time tour that teaches search: the keys that open it, that it
 * searches the studio's content and not only its pages, and the letters that
 * jump straight to a page. Shown once per browser.
 *
 * @param {object} props
 * @param {React.RefObject} props.searchButtonRef  the header's search button
 * @param {React.RefObject} props.menuRef  trigger of the menu whose shortcuts the last step shows
 * @param {Array} props.menuShortcuts  that menu's shortcuts (collectNavShortcuts)
 * @param {boolean} props.enabled  whether this page may show it
 */
export default function SearchShortcutsTip({
  searchButtonRef,
  menuRef,
  menuShortcuts,
  enabled,
}) {
  const apple = useApplePlatform();
  const [hasKeyboard, setHasKeyboard] = useState(false);
  const { showAnnouncement, markAsSeen } = useFeatureAnnouncement(
    TOUR_ID,
    enabled && hasKeyboard,
  );

  useEffect(() => {
    setHasKeyboard(window.matchMedia?.(KEYBOARD_SCREEN).matches ?? false);
  }, []);

  const searchCombo = searchKeys(apple);
  const [search] = searchCombo;
  const [example, secondExample] = menuShortcuts;

  const steps = [
    {
      targetRef: searchButtonRef,
      title: `Find anything with ${searchCombo.join(" ")}`,
      description: (
        <>
          Hold <Kbd>{search}</Kbd>
          {apple ? " Command" : ""} and tap <Kbd>K</Kbd> on any page — or click
          this button.
        </>
      ),
    },
    {
      targetRef: searchButtonRef,
      title: "More than pages",
      description: (
        <>
          Type a few letters. As well as pages, it searches your studio&apos;s
          assignments, learning paths, tests and resources. Press{" "}
          <Kbd>Enter</Kbd> to open the one you want.
        </>
      ),
    },
  ];

  if (example) {
    const [option] = pageShortcutKeys(example.letter, apple);
    steps.push({
      targetRef: menuRef,
      title: "Jump straight to a page",
      description: (
        <>
          Open this menu and you&apos;ll see keys beside the pages people use
          most. Hold <Kbd>{option}</Kbd>
          {apple ? " Option" : ""} and tap <Kbd>{example.letter}</Kbd> to go
          straight to {example.label}
          {secondExample ? (
            <>
              , or{" "}
              <KeyCombo keys={pageShortcutKeys(secondExample.letter, apple)} />{" "}
              for {secondExample.label}
            </>
          ) : null}
          .
        </>
      ),
    });
  }

  return (
    <FeatureAnnouncement
      isOpen={showAnnouncement && enabled}
      onClose={markAsSeen}
      steps={steps}
      eyebrow="Search & shortcuts"
    />
  );
}
